/**
 * ============================================
 * Family Chart Adapter — JEFA Tree JSON → Rows
 * ============================================
 *
 * Purpose:
 * Convert the nested tree JSON returned by:
 *   GET /lineages/:id/tree_json
 *
 * into the flat "rows" structure required by the
 * `family-chart` library.
 *
 * This file powers:
 *   /lineages/:id/family_chart
 *
 * Do not confuse this with:
 *   /people/:id/graph_explorer
 *   /lineages/:id/family_tree
 *
 * --------------------------------------------
 * INPUT (Rails JSON shape)
 * --------------------------------------------
 *
 * A recursive object starting at payload.node:
 *
 * {
 *   id: 108,
 *   display_name: "Michelle Hartley",
 *   gender: "female",
 *   parents: {
 *     father: {
 *       relationship_type: "bio",
 *       node: { ...same shape... }
 *     },
 *     mother: {
 *       relationship_type: "bio",
 *       node: { ...same shape... }
 *     }
 *   },
 *   children: [
 *     {
 *       relationship_type: "bio",
 *       parent_role: "mother",
 *       node: { ...same shape... }
 *     }
 *   ]
 * }
 *
 * --------------------------------------------
 * OUTPUT (family-chart rows)
 * --------------------------------------------
 *
 * Array of flat nodes:
 *
 * [
 *   {
 *     id: "108",
 *     data: {
 *       id: "108",
 *       gender: "female",
 *       "first name": "Michelle",
 *       "last name": "Hartley",
 *       birthday: "...",
 *       "birth place": "...",
 *       deathday: "..."
 *     },
 *     rels: {
 *       parents: ["109"],
 *       children: ["399"],
 *       spouses: []
 *     }
 *   }
 * ]
 *
 * --------------------------------------------
 * IMPORTANT NOTES
 * --------------------------------------------
 *
 * - This is custom JEFA glue code.
 * - It is not part of the family-chart library.
 * - Rails sends nested tree JSON.
 * - Family Chart needs flat rows.
 * - Family Chart 0.9.0 can throw:
 *     "child has more than 1 parent"
 *   so this adapter trims child rows to one primary parent
 *   after using the full parent list to infer spouses/co-parents.
 */
// import 'family-chart/dist/styles/family-chart.css';
// import * as f3 from "family-chart"


//  * ============================================
//  * Family Chart Adapter — JEFA Tree JSON → Rows
//  * ============================================
//  *
//  * Purpose:
//  * Convert the nested tree JSON returned by:
//  *   GET /lineages/:id/tree_json
//  *
//  * into the flat "rows" structure required by the
//  * `family-chart` library.
//  *
//  * This file powers:
//  *   /lineages/:id/family_chart
//  *
//  * Do not confuse this with:
//  *   /people/:id/graph_explorer
//  *   /lineages/:id/family_tree
//  */

// IMPORTANT:
// This CSS import is intentionally commented out for now.
// Importing package CSS from JS may cause esbuild to emit CSS that collides
// with the PostCSS-built app/assets/builds/application.css.
//
import * as f3 from 'family-chart';
import "family-chart/styles/family-chart.css"

console.log("[family-chart] ADAPTER FILE LOADED")

async function fetchTreeData(lineageId, depth) {
  const url = `/lineages/${lineageId}/tree_json?depth=${depth}&include_children=1`

  console.log("[family-chart] fetch url", url)

  const response = await fetch(url, {
    headers: { Accept: "application/json" }
  })

  console.log("[family-chart] fetch response", response.status, response.ok)

  if (!response.ok) {
    throw new Error(`Tree JSON request failed: ${response.status}`)
  }

  const json = await response.json()

  console.log("[family-chart] fetch json parsed")

  return json
}


function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;")
}

function styleFamilyChartLinks(element) {
  element.querySelectorAll("path.link, .links_view path.link").forEach((link) => {
    link.setAttribute("stroke", "#9fb0bf")
    link.setAttribute("stroke-width", "2.5")
    link.setAttribute("fill", "none")
    link.style.opacity = "0.9"
    link.style.transition = "none"
  })
}

// function positionFamilyChartView(element, { x = 80, y = 80, scale = 1 } = {}) {
//   const transform = `translate(${x}px, ${y}px) scale(${scale})`

//   const svgView = element.querySelector("svg.main_svg g.view")
//   const htmlView = element.querySelector("#htmlSvg .cards_view")

//   if (svgView) {
//     svgView.setAttribute("style", `transform: ${transform};`)
//   }

//   if (htmlView) {
//     htmlView.style.transformOrigin = "0px 0px 0px"
//     htmlView.style.transform = transform
//   }

//   console.log("[family-chart] custom viewport", { x, y, scale })
// }

async function mountFamilyChart(element) {
  const statusEl = document.querySelector('[data-family-chart-target="status"]')

  try {
    console.log("[family-chart] CP74 JEFA Donatso mount start")

    if (statusEl) {
      statusEl.textContent = "Loading JEFA family chart data…"
    }

    const lineageId = element.dataset.lineageId
    const depth = element.dataset.depth || 3

    const payload = await fetchTreeData(lineageId, depth)

    console.log("[family-chart] CP74 JEFA payload", payload)
    console.log("[family-chart] CP74 JEFA root node", payload.node)

    const rows = adaptTreeToFamilyChart(payload.node)

    addBidirectionalFamilyRels(rows)

    console.log("[family-chart] CP74 JEFA rows", rows)
    console.table(rows.map((row) => ({
      id: row.id,
      name: `${row.data?.["first name"] || ""} ${row.data?.["last name"] || ""}`.trim(),
      gender: row.data?.gender || "",
      parents: row.rels?.parents?.join(", ") || "",
      spouses: row.rels?.spouses?.join(", ") || "",
      children: row.rels?.children?.join(", ") || ""
    })))

    element.innerHTML = ""

    const rowsById = new Map(rows.map((row) => [String(row.id), row]))

    const f3Chart = f3.createChart("#FamilyChart", rows)

    console.log("[family-chart] chart api methods", Object.keys(f3Chart).sort())

    f3Chart
      .setCardHtml()
      .setStyle("rect")
      .setCardDim({
        width: 420,
        height: 170
      })
      .setCardInnerHtmlCreator((d) => {
        const rowId = String(d?.tid || d?.id || d?.data?.id || d?.data?.data?.id || "")
        const row = rowsById.get(rowId)
        const person = row?.data || d?.data?.data || d?.data || {}

        const firstName = person["first name"] || ""
        const lastName = person["last name"] || ""
        const fullName = [firstName, lastName].filter(Boolean).join(" ")
        const dates = person.birthday || ""

        return `
          <div style="
            box-sizing: border-box;
            width: 100%;
            height: 100%;
            padding: 14px 16px;
            border-radius: 0;
            background: transparent;
            border: 0;
            box-shadow: none;
            display: flex;
            flex-direction: column;
            justify-content: center;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          ">
            <div style="
              font-size: 20px;
              font-weight: 700;
              line-height: 1.2;
              color: #1f2933;
              text-align: center;
            ">
              ${escapeHtml(fullName)}
            </div>

            <div style="
              margin-top: 8px;
              font-size: 15px;
              line-height: 1.2;
              color: #64748b;
              text-align: center;
            ">
              ${escapeHtml(dates)}
            </div>
          </div>
        `
      })

    f3Chart
      .setCardXSpacing(460)
      .setCardYSpacing(240)
      .setSingleParentEmptyCard(false)

    f3Chart.updateTree({
      initial: false,
      tree_position: "main_to_middle"
    })

      setTimeout(() => {
      styleFamilyChartLinks(element)
    }, 150)

    // setTimeout(() => {
    //   positionFamilyChartView(element, {
    //     x: -250,
    //     y: -120,
    //     scale: 1
    //   })

    //   styleFamilyChartLinks(element)
    // }, 200)
  

    

    const loading = document.getElementById("chart-loading")
    if (loading) loading.style.display = "none"

    if (statusEl) {
      statusEl.textContent = "JEFA Donatso chart rendered."
    }
  } catch (error) {
    console.error("[family-chart] JEFA chart error", error)

    if (statusEl) {
      statusEl.textContent = `family-chart error: ${error.message}`
    }
  }
}

function adaptTreeToFamilyChart(node, rows = [], seen = new Set()) {
  if (!node || seen.has(String(node.id))) return rows

  seen.add(String(node.id))

  const father = node.parents?.father?.node
  const mother = node.parents?.mother?.node

  const parentIds = [father?.id, mother?.id]
    .filter(Boolean)
    .map(String)

  const children = Array.isArray(node.children) ? node.children : []

  const childIds = children
    .map(entry => entry?.node?.id)
    .filter(Boolean)
    .map(String)

  const rels = {}

  if (parentIds.length) rels.parents = parentIds
  if (childIds.length) rels.children = childIds

  const fullName = node.display_name || `Person #${node.id}`
  const parts = fullName.trim().split(/\s+/)
  const lastName = parts.length > 1 ? parts.pop() : ""
  const firstName = parts.join(" ") || fullName

  rows.push({
    id: String(node.id),
    data: {
      id: String(node.id),
      gender: normalizeGender(node.gender),
      "first name": firstName,
      "last name": lastName,
      birthday: node.birth_date_text || node.life_dates || "",
      "birth place": node.birth_place || "",
      deathday: node.death_date_text || "",
      "death place": node.death_place || ""
    },
    rels: rels
  })

  if (father) adaptTreeToFamilyChart(father, rows, seen)
  if (mother) adaptTreeToFamilyChart(mother, rows, seen)

  console.log("[family-chart] children for", node.id, children.map(entry => ({
    child_id: entry?.node?.id,
    child_name: entry?.node?.display_name
  })))

  children.forEach(childEntry => {
    const child = childEntry?.node
    if (child) adaptTreeToFamilyChart(child, rows, seen)
  })

  return rows
}

  function addBidirectionalFamilyRels(rows) {
  const rowsById = new Map(rows.map(row => [String(row.id), row]))

  rows.forEach(row => {
    row.rels = row.rels || {}
    row.rels.parents = uniqueStrings(row.rels.parents || [])
    row.rels.children = uniqueStrings(row.rels.children || [])
    row.rels.spouses = uniqueStrings(row.rels.spouses || [])
  })

  rows.forEach(childRow => {
    const childId = String(childRow.id)
    const parentIds = uniqueStrings(childRow.rels.parents || [])

    parentIds.forEach(parentId => {
      const parentRow = rowsById.get(parentId)
      if (!parentRow) return

      addRel(parentRow, "children", childId)
    })

    if (parentIds.length >= 2) {
      for (let i = 0; i < parentIds.length; i += 1) {
        for (let j = i + 1; j < parentIds.length; j += 1) {
          const firstParentId = parentIds[i]
          const secondParentId = parentIds[j]

          const firstParent = rowsById.get(firstParentId)
          const secondParent = rowsById.get(secondParentId)

          if (!firstParent || !secondParent) continue

          addRel(firstParent, "spouses", secondParentId)
          addRel(secondParent, "spouses", firstParentId)
        }
      }
    }
  })

  // Family Chart 0.9.0 may throw:
  //   "child has more than 1 parent"
  //
  // We first use the full parent list to infer spouse/co-parent links.
  // Then we trim the child row down to one primary parent for library compatibility.
  rows.forEach(row => {
    if ((row.rels.parents || []).length > 1) {
      console.warn("[family-chart] trimming parents for", row.id, row.rels.parents)
      row.rels.parents = [row.rels.parents[0]]
    }
  })

  console.log("[family-chart] rows with multiple parents after trim",
    rows.filter(row => (row.rels?.parents || []).length > 1)
  )
}

function addRel(row, relName, id) {
  if (!row || !id) return

  row.rels = row.rels || {}
  row.rels[relName] = row.rels[relName] || []

  const stringId = String(id)

  if (!row.rels[relName].includes(stringId)) {
    row.rels[relName].push(stringId)
  }
}

function normalizeGender(gender) {
  const value = String(gender || "").toLowerCase()

  if (value === "male" || value === "m") return "M"
  if (value === "female" || value === "f") return "F"

  return "U"
}

function uniqueStrings(values) {
  return Array.from(
    new Set(
      values
        .map(value => String(value))
        .filter(value => value.length > 0)
    )
  )
}

function initFamilyChart() {
  console.log("[family-chart] init fired")

  const element = document.querySelector("[data-family-chart-root]")

  console.log("[family-chart] element found?", !!element, element)

  if (!element) {
    console.log("[family-chart] no family chart root on this page")
    return
  }

  console.log("[family-chart] element dataset", element.dataset)

  if (element.dataset.familyChartMounted === "true") {
    console.log("[family-chart] already mounted, skipping")
    return
  }

  element.dataset.familyChartMounted = "true"

  console.log("[family-chart] calling mountFamilyChart immediately")

  mountFamilyChart(element)
}

document.addEventListener("turbo:load", initFamilyChart)
document.addEventListener("DOMContentLoaded", initFamilyChart)